import React, { useState } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Table,
  ActionIcon,
  Badge,
  Text,
  TextInput,
  LoadingOverlay,
  Avatar,
  Tooltip,
  Pagination,
  Select,
  Stack,
  Flex,
  Card,
  Menu,
  rem,
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconEye,
  IconSearch,
  IconUsers,
  IconSchool,
  IconCake,
  IconDownload,
  IconDots,
  IconTrash,
  IconPhoto,
  IconBrain,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useStudents } from '../../hooks/useStudents';
import { useSchools } from '../../hooks/useSchools';
import type { Student } from '../../types/api';

const Students: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState('25');
  const [sortBy, setSortBy] = useState('first_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [schoolFilter, setSchoolFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const {
    data: studentsData,
    isLoading,
    error,
  } = useStudents({
    page,
    per_page: parseInt(perPage),
    search: debouncedSearch,
    sort_by: sortBy,
    sort_order: sortOrder,
    filters: {
      ...(statusFilter && { enrollment_status: statusFilter }),
      ...(schoolFilter && { school_id: parseInt(schoolFilter) }),
    },
  });

  const { data: schoolsData } = useSchools({
    per_page: 100, // Get all schools for filter
  });

  // Extract data from the API response
  const students = studentsData?.students || [];
  const pagination = studentsData?.pagination;
  const schools = schoolsData?.schools || [];

  const handleCreateStudent = () => {
    navigate('/admin/students/new');
  };

  const handleEditStudent = (student: Student) => {
    navigate(`/admin/students/${student.id}/edit`);
  };

  const handleViewStudent = (student: Student) => {
    navigate(`/admin/students/${student.id}`);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      enrolled: 'green',
      pending: 'yellow',
      graduated: 'blue',
      transferred: 'orange',
      withdrawn: 'red',
      suspended: 'red',
    };
    return colors[status] || 'gray';
  };


  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatAge = (birthDate: string) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1;
    }
    return age;
  };

  if (error) {
    notifications.show({
      title: 'Error',
      message: 'Failed to load students. Please try again.',
      color: 'red',
    });
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2}>Students</Title>
          <Text size="sm" c="dimmed">
            Manage student enrollment and face recognition
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={handleCreateStudent}
        >
          Add Student
        </Button>
      </Group>

      {/* Filters and Search */}
      <Card>
        <Stack gap="md">
          <Group>
            <TextInput
              placeholder="Search students..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flexGrow: 1, maxWidth: 400 }}
            />
            <Select
              placeholder="Filter by school"
              data={[
                { value: '', label: 'All Schools' },
                ...schools
                  .filter((school: any) => school && school.name)
                  .map((school: any) => ({
                    value: school.id.toString(),
                    label: school.name,
                  })),
              ]}
              value={schoolFilter}
              onChange={(value) => setSchoolFilter(value || '')}
              style={{ minWidth: 200 }}
            />
            <Select
              placeholder="Filter by status"
              data={[
                { value: '', label: 'All Status' },
                { value: 'enrolled', label: 'Enrolled' },
                { value: 'pending', label: 'Pending' },
                { value: 'graduated', label: 'Graduated' },
                { value: 'transferred', label: 'Transferred' },
                { value: 'withdrawn', label: 'Withdrawn' },
                { value: 'suspended', label: 'Suspended' },
              ]}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || '')}
              style={{ minWidth: 150 }}
            />
          </Group>

          <Group justify="flex-end">
            <Select
              label="Per page"
              value={perPage}
              onChange={(value) => {
                setPerPage(value || '25');
                setPage(1);
              }}
              data={[
                { value: '10', label: '10' },
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
              ]}
              style={{ width: 100 }}
            />
          </Group>
        </Stack>
      </Card>

      {/* Students Table */}
      <Paper withBorder>
        <LoadingOverlay visible={isLoading} />

        {students.length === 0 && !isLoading ? (
          <Flex direction="column" align="center" justify="center" py="xl">
            <IconUsers size={48} color="gray" />
            <Title order={4} c="dimmed" mt="md">
              No students found
            </Title>
            <Text size="sm" c="dimmed" ta="center" mt="xs">
              {search || schoolFilter || statusFilter
                ? 'Try adjusting your search criteria'
                : 'Get started by enrolling your first student'}
            </Text>
            {!search && !schoolFilter && !statusFilter && (
              <Button
                variant="outline"
                leftSection={<IconPlus size={16} />}
                onClick={handleCreateStudent}
                mt="md"
              >
                Add Student
              </Button>
            )}
          </Flex>
        ) : (
          <>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('first_name')}
                  >
                    Student {getSortIcon('first_name')}
                  </Table.Th>
                  <Table.Th>School & Class</Table.Th>
                  <Table.Th>Age & Gender</Table.Th>
                  <Table.Th>Face Recognition</Table.Th>
                  <Table.Th>Parents</Table.Th>
                  <Table.Th
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('enrollment_date')}
                  >
                    Enrollment {getSortIcon('enrollment_date')}
                  </Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th style={{ width: 120 }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {students.map((student: any) => (
                  <Table.Tr key={student.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar size="md" radius="xl">
                          {student.first_name?.[0]}{student.last_name?.[0]}
                        </Avatar>
                        <div>
                          <Text fw={500} size="sm">
                            {student.first_name} {student.last_name}
                          </Text>
                          {student.nickname && (
                            <Text size="xs" c="dimmed">
                              "{student.nickname}"
                            </Text>
                          )}
                          <Text size="xs" c="dimmed">
                            ID: {student.student_id}
                          </Text>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Group gap={4}>
                          <IconSchool size={12} />
                          <Text size="xs">{student.school_info?.name || 'No school'}</Text>
                        </Group>
                        {student.current_class && (
                          <Group gap={4}>
                            <IconUsers size={12} />
                            <Text size="xs">{student.current_class.name}</Text>
                          </Group>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Group gap={4}>
                          <IconCake size={12} />
                          <Text size="xs">{student.birth_date ? `${formatAge(student.birth_date)} years old` : 'Age unknown'}</Text>
                        </Group>
                        {student.gender && (
                          <Text size="xs" c="dimmed" tt="capitalize">
                            {typeof student.gender === 'object' ? student.gender.label : student.gender}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {student.face_recognition?.recognition_ready ? (
                          <Badge
                            color="green"
                            variant="light"
                            size="xs"
                          >
                            Trained
                          </Badge>
                        ) : (
                          <Badge
                            color="gray"
                            variant="light"
                            size="xs"
                          >
                            Not trained
                          </Badge>
                        )}
                        <Text size="xs" c="dimmed">
                          {student.face_recognition?.photos_count || 0} photos
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {student.parents_summary?.total_count || 0} parent(s)
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {student.enrollment_date ? new Date(student.enrollment_date).toLocaleDateString() : 'Not set'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {student.enrollment_status ? (
                        <Badge
                          color={getStatusColor(typeof student.enrollment_status === 'object' ? student.enrollment_status.value : student.enrollment_status)}
                          variant="light"
                          size="sm"
                        >
                          {typeof student.enrollment_status === 'object' ? student.enrollment_status.label : student.enrollment_status}
                        </Badge>
                      ) : (
                        <Text size="xs" c="dimmed">No status</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label="View details">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleViewStudent(student)}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Edit student">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleEditStudent(student)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Menu shadow="md" width={200}>
                          <Menu.Target>
                            <ActionIcon variant="subtle" size="sm">
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconPhoto style={{ width: rem(14), height: rem(14) }} />}
                              onClick={() => navigate(`/admin/students/${student.id}/photos`)}
                            >
                              Manage Photos
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconBrain style={{ width: rem(14), height: rem(14) }} />}
                              onClick={() => navigate(`/admin/students/${student.id}/face-recognition`)}
                            >
                              Face Recognition
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconDownload style={{ width: rem(14), height: rem(14) }} />}
                            >
                              Export Data
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                              leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                              color="red"
                            >
                              Delete Student
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <Group justify="center" p="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={pagination.total_pages}
                  size="sm"
                />
              </Group>
            )}
          </>
        )}
      </Paper>

      {/* Summary Stats */}
      {pagination && (
        <Text size="sm" c="dimmed" ta="center">
          Showing {students.length} of {pagination.total_count} students
          {(search || schoolFilter || statusFilter) && ' (filtered)'}
        </Text>
      )}
    </Stack>
  );
};

export default Students;
